import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-6" }
const _hoisted_2 = { class: "m-0 rounded-lg border border-gray-200 bg-[#f9fafb] px-6 py-5 text-[17px] leading-7 text-gray-800" }
const _hoisted_3 = {
  key: 0,
  "data-errors": "",
  role: "alert",
  class: "m-0 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
}
const _hoisted_4 = {
  key: 1,
  class: "m-0 rounded-md border border-[#fde68a] bg-[#fffbeb] px-3.5 py-3 text-sm text-[#92400e]"
}
const _hoisted_5 = { key: 2 }
const _hoisted_6 = { class: "flex flex-col gap-2" }
const _hoisted_7 = { class: "flex items-center gap-2.5 border-b border-gray-200 bg-[#f9fafb] px-3.5 py-2.5" }
const _hoisted_8 = { class: "inline-grid h-8 w-8 place-items-center rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-800" }
const _hoisted_9 = { class: "text-sm font-semibold text-gray-800" }
const _hoisted_10 = { class: "w-32 flex-none text-gray-500" }
const _hoisted_11 = { class: "min-w-0 flex-1 text-gray-900" }
const _hoisted_12 = { class: "rounded-lg border border-gray-300" }
const _hoisted_13 = { class: "pt-0.5 text-[#10b981]" }
const _hoisted_14 = { class: "min-w-0" }
const _hoisted_15 = { class: "text-sm font-semibold text-gray-900" }
const _hoisted_16 = { class: "text-[13px] text-gray-600" }

import { computed } from 'vue'

import HighlightedText from '@/components/neue/wizard/HighlightedText'
import WizardIcon from '@/components/neue/wizard/WizardIcon.vue'
import { injectWizard } from '@/composables/useWizardContext'
import { reminderBody } from '@/config/dropWizardSteps'
import { formatDate, formatLongDate, formatTime } from '@/helpers/dropWizardDates'
import { reminderForIndex, reminderSendsAt } from '@/helpers/dropWizardPreview'
import { audienceText, sentence as buildSentence, whatWeSetUp } from '@/helpers/dropWizardSummary'


export default {
  __name: 'StepReview',
  props: {
  stores: { type: Array, default: () => [] },
  methods: { type: Array, default: () => [] },
  tiers: { type: Array, default: () => [] },
  tz: { type: String, default: undefined },
  errors: { type: Array, default: () => [] }
},
  setup(__props) {

const props = __props
const CHAIN_ESTIMATE = { weekly: 26, biweekly: 13, four_weekly: 6, once: 1 }

const wizard = injectWizard()
const preview = computed(() => wizard.preview.value)
const reminderIds = computed(() => wizard.previewReminderIds?.value ?? [])

const sentence = computed(() => buildSentence(wizard.data, { stores: props.stores, methods: props.methods }))
const store = computed(() => props.stores.find(s => s.id === String(wizard.data.storeId)))
const method = computed(() =>
  wizard.data.fulfillment.newMethod
    ? { name: wizard.data.fulfillment.newMethod.name.trim(), type: wizard.data.fulfillment.newMethod.type }
    : props.methods.find(m => m.id === wizard.data.fulfillment.methodId)
)
const pickup = computed(() => method.value?.type === 'pickup')

const at = iso => `${formatDate(iso, props.tz)}, ${formatTime(iso, props.tz)}`

const productionRow = occurrence => ({
  key: 'Production',
  value: occurrence.production_dates.map(date => formatDate(date, props.tz)).join(' and ')
})

const reminderRows = occurrence =>
  (occurrence.reminders ?? []).flatMap(entry => {
    const reminder = reminderForIndex(wizard.data.reminders, reminderIds.value, entry.index)
    if (!entry.sends_at || !reminder) return []
    const text = reminderBody(reminder, wizard.data).replace(/\{\{first_name\}\}/g, 'Sam')
    return [
      {
        key: 'Text reminder',
        value: `${at(entry.sends_at)} · "${text.length > 48 ? `${text.slice(0, 48)}...` : text}"`
      }
    ]
  })

const cardRows = occurrence => {
  if (occurrence.closed) return [productionRow(occurrence), { key: 'Ordering', value: 'Closed day, no ordering' }]
  const rows = [
    {
      key: 'Ordering',
      value: `${occurrence.opens_at ? at(occurrence.opens_at) : 'Now'} to ${at(occurrence.closes_at)}`
    }
  ]
  if (occurrence.autocharge_at) rows.push({ key: 'Auto-charge', value: at(occurrence.autocharge_at) })
  rows.push(productionRow(occurrence))
  if (occurrence.arrival)
    rows.push({
      key: pickup.value ? 'Pickup' : 'Delivery',
      value: `${formatDate(occurrence.arrival_date, props.tz)}, ${formatTime(occurrence.arrival[0], props.tz)} to ${formatTime(occurrence.arrival[1], props.tz)}`
    })
  return [...rows, ...reminderRows(occurrence)]
}

const cards = computed(() =>
  (preview.value?.occurrences ?? []).slice(0, 3).map(occurrence => {
    const first = occurrence.production_dates[0]
    return { number: Number(first.slice(-2)), title: formatLongDate(first, props.tz), rows: cardRows(occurrence) }
  })
)

const skippedNote = computed(() => {
  const days = preview.value?.skipped_closed_days ?? []
  if (!days.length) return ''
  return `${days.map(day => formatDate(day.date, props.tz)).join(', ')} ${days.length === 1 ? 'is a closed day' : 'are closed days'} for ${store.value?.name ?? 'your store'}. Ordering is off then and no reminders go out.`
})

// Without a preview yet, every reminder is assumed to send.
const sendingCount = computed(() =>
  preview.value
    ? wizard.data.reminders.filter(reminder => reminderSendsAt(preview.value, reminderIds.value, reminder.id)).length
    : wizard.data.reminders.length
)

const creates = computed(() =>
  whatWeSetUp(wizard.data, {
    method: method.value,
    audienceText: audienceText(wizard.data, props.tiers).toLowerCase(),
    dateCount: CHAIN_ESTIMATE[wizard.data.rhythm] ?? 0,
    storeName: store.value?.name ?? 'your store',
    sendingCount: sendingCount.value
  })
)

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("p", _hoisted_2, [
      _createVNode(_unref(HighlightedText), { text: sentence.value }, null, 8, ["text"])
    ]),
    (__props.errors.length)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.errors, (error) => {
            return (_openBlock(), _createElementBlock("li", { key: error }, _toDisplayString(error), 1))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (skippedNote.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(skippedNote.value), 1))
      : _createCommentVNode("", true),
    (cards.value.length)
      ? (_openBlock(), _createElementBlock("section", _hoisted_5, [
          _cache[0] || (_cache[0] = _createElementVNode("h3", { class: "m-0 mb-2 text-base font-bold text-gray-800" }, "The first few dates", -1)),
          _createElementVNode("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cards.value, (card) => {
              return (_openBlock(), _createElementBlock("div", {
                key: card.title,
                "data-date-card": "",
                class: "overflow-hidden rounded-lg border border-gray-300"
              }, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(card.number), 1),
                  _createElementVNode("span", _hoisted_9, _toDisplayString(card.title), 1)
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(card.rows, (row) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: row.key + row.value,
                    class: "flex items-center gap-2.5 border-t border-[#f3f4f6] px-3.5 py-2 text-sm"
                  }, [
                    _createElementVNode("span", _hoisted_10, _toDisplayString(row.key), 1),
                    _createElementVNode("span", _hoisted_11, _toDisplayString(row.value), 1)
                  ]))
                }), 128))
              ]))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("section", null, [
      _cache[1] || (_cache[1] = _createElementVNode("h3", { class: "m-0 mb-2 text-base font-bold text-gray-800" }, "What we'll set up", -1)),
      _createElementVNode("div", _hoisted_12, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(creates.value, (item, i) => {
          return (_openBlock(), _createElementBlock("div", {
            key: item.title,
            class: _normalizeClass(["flex items-start gap-3 px-3.5 py-3", i ? 'border-t border-[#f3f4f6]' : ''])
          }, [
            _createElementVNode("span", _hoisted_13, [
              _createVNode(WizardIcon, { name: "check" })
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("div", _hoisted_15, [
                _createVNode(_unref(HighlightedText), {
                  text: item.title
                }, null, 8, ["text"])
              ]),
              _createElementVNode("div", _hoisted_16, _toDisplayString(item.text), 1)
            ])
          ], 2))
        }), 128))
      ])
    ])
  ]))
}
}

}